import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { Box, Link as MuiLink } from '@mui/material'
import type { Theme } from '@mui/material/styles'

export interface ProductUpdateMarkdownProps {
  /** Markdown source; empty renders nothing. */
  markdown: string
  /** When true, constrain headings so release titles do not blow up hierarchy. */
  titleMode?: boolean
  /** Optional sx on the outer Box. */
  sx?: object
}

function markdownContainerSx(theme: Theme, titleMode: boolean) {
  return {
    color: 'text.primary',
    '& p': {
      ...theme.typography.body2,
      margin: titleMode ? 0 : '0.5em 0',
      lineHeight: 1.75,
    },
    '& h1': {
      ...theme.typography.h5,
      fontWeight: 700,
      mt: titleMode ? 0 : 1,
      mb: titleMode ? 0 : 0.5,
    },
    '& h2': {
      ...theme.typography.h6,
      fontWeight: 700,
      mt: titleMode ? 0 : 1,
      mb: titleMode ? 0 : 0.5,
    },
    '& h3': {
      ...theme.typography.subtitle1,
      fontWeight: 700,
      mt: titleMode ? 0 : 0.75,
      mb: titleMode ? 0 : 0.25,
    },
    '& ul, & ol': {
      ...theme.typography.body2,
      pl: 2.5,
      my: titleMode ? 0 : 0.5,
    },
    '& li': { my: 0.25 },
    '& code': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: '0.85em',
      bgcolor: 'action.hover',
      px: 0.5,
      py: 0.125,
      borderRadius: 0.5,
    },
    '& pre': {
      bgcolor: 'action.hover',
      p: 1.5,
      borderRadius: 1,
      overflow: 'auto',
      my: 1,
      '& code': { bgcolor: 'transparent', p: 0 },
    },
    '& blockquote': {
      borderLeft: '3px solid',
      borderColor: 'divider',
      pl: 1.5,
      my: 1,
      color: 'text.secondary',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      my: 1,
      ...theme.typography.body2,
    },
    '& th, & td': {
      border: '1px solid',
      borderColor: 'divider',
      px: 1,
      py: 0.5,
      textAlign: 'left',
    },
    '& th': { fontWeight: 600, bgcolor: 'action.hover' },
  } as const
}

/**
 * Sanitized GitHub-flavored markdown for product updates (releases + roadmap).
 */
export function ProductUpdateMarkdown({ markdown, titleMode = false, sx: sxProp }: ProductUpdateMarkdownProps) {
  const trimmed = markdown?.trim() ?? ''
  if (!trimmed) return null

  return (
    <Box
      className="product-update-markdown"
      sx={(theme) => ({
        ...markdownContainerSx(theme, titleMode),
        ...(sxProp as object),
      })}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ href, children }) =>
            href ? (
              <MuiLink
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {children}
              </MuiLink>
            ) : (
              <Box component="span">{children}</Box>
            ),
        }}
      >
        {trimmed}
      </ReactMarkdown>
    </Box>
  )
}
